import { useState, useEffect, useMemo } from 'react'
import { MessageSquare, Users, School, Loader2, Calendar, FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { toast } from 'sonner'

// CORRECT localStorage keys for your app
const SCHOOL_ID_KEY = 'lybibal-selected-school-id'
const YEAR_ID_KEY = 'lybibal-selected-year-id'
const YEAR_LABEL_KEY = 'lybibal-selected-year-label'

interface CouncilDecision {
  id: number
  student_id: number
  student_name: string
  class_id: number
  class_name: string
  decision: 'passage' | 'redoublement' | 'exclusion' | 'blame' | 'encouragement'
  decision_label: string
  period: string
  date: string
  observations?: string
  school_id: number
  academic_year_id: number
  created_at: string
}

interface ClassCouncil {
  id: number
  class_id: number
  class_name: string
  level_name: string
  cycle_name: string
  meeting_date: string
  period: 'sequence1' | 'sequence2' | 'sequence3' | 'sequence4' | 'sequence5' | 'sequence6' | 'annuel'
  status: 'planned' | 'held' | 'archived'
  decisions_count: number
  school_id: number
  academic_year_id: number
  created_at: string
}

function cn(...classes: (string | false | null | undefined)[]) { return classes.filter(Boolean).join(' ') }

function getDecisionColor(decision: string): string {
  switch (decision) {
    case 'passage': return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'redoublement': return 'bg-amber-50 text-amber-700 border-amber-200'
    case 'exclusion': return 'bg-red-50 text-red-700 border-red-200'
    case 'blame': return 'bg-orange-50 text-orange-700 border-orange-200'
    case 'encouragement': return 'bg-blue-50 text-blue-700 border-blue-200'
    default: return 'bg-gray-50 text-gray-700 border-gray-200'
  }
}

function getDecisionIcon(decision: string) {
  switch (decision) {
    case 'passage': return <CheckCircle className="w-4 h-4" />
    case 'redoublement': return <AlertTriangle className="w-4 h-4" />
    case 'exclusion': return <XCircle className="w-4 h-4" />
    default: return <FileText className="w-4 h-4" />
  }
}

export default function AcademicCouncilPage() {
  const { lang } = useLang()
  const [loading, setLoading] = useState(true)
  const [councils, setCouncils] = useState<ClassCouncil[]>([])
  const [decisions, setDecisions] = useState<CouncilDecision[]>([])
  const [activeTab, setActiveTab] = useState<'councils' | 'decisions'>('councils')
  const [searchTerm, setSearchTerm] = useState('')

  // Get school_id and academic_year_id from localStorage using CORRECT keys
  const selectedSchoolId = useMemo(() => {
    const stored = localStorage.getItem(SCHOOL_ID_KEY)
    if (stored) {
      const val = Number(stored)
      if (!isNaN(val) && val > 0) return val
    }
    // Fallback: try auth-storage (Zustand format)
    const auth = localStorage.getItem('auth-storage')
    if (auth) {
      try {
        const parsed = JSON.parse(auth)
        const sid = parsed?.state?.user?.school_id || parsed?.state?.school_id
        if (sid) return Number(sid)
      } catch { /* ignore */ }
    }
    return null
  }, [])

  const selectedAcademicYearId = useMemo(() => {
    const stored = localStorage.getItem(YEAR_ID_KEY)
    if (stored) {
      const val = Number(stored)
      if (!isNaN(val) && val > 0) return val
    }
    // Fallback: try lybibal-academic-year
    const ay = localStorage.getItem('lybibal-academic-year')
    if (ay) {
      const val = Number(ay)
      if (!isNaN(val) && val > 0) return val
    }
    // Fallback: try auth-storage
    const auth = localStorage.getItem('auth-storage')
    if (auth) {
      try {
        const parsed = JSON.parse(auth)
        const yid = parsed?.state?.user?.academic_year_id || parsed?.state?.academic_year_id
        if (yid) return Number(yid)
      } catch { /* ignore */ }
    }
    return null
  }, [])

  const selectedYearLabel = useMemo(() => {
    return localStorage.getItem(YEAR_LABEL_KEY) || ''
  }, [])

  // Listen for academic year changes from other components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SCHOOL_ID_KEY || e.key === YEAR_ID_KEY || e.key === 'lybibal-academic-year') {
        window.location.reload()
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Load data filtered by school and year
  const loadData = useMemo(() => async () => {
    if (!selectedSchoolId || !selectedAcademicYearId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      // TODO: Replace with actual API calls when backend endpoints are ready
      // const councilsData = await councilApi.getCouncils(selectedSchoolId, selectedAcademicYearId)
      // const decisionsData = await councilApi.getDecisions(selectedSchoolId, selectedAcademicYearId)
      // setCouncils(councilsData || [])
      // setDecisions(decisionsData || [])
      
      // For now, empty state until backend is ready
      setCouncils([])
      setDecisions([])
    } catch (err) {
      console.error('Error loading council data:', err)
      toast.error(lang === 'fr' ? 'Erreur chargement' : 'Loading error')
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId, selectedAcademicYearId, lang])

  useEffect(() => {
    loadData()
  }, [loadData])

  const filteredCouncils = useMemo(() => {
    if (!searchTerm) return councils
    return councils.filter(c => 
      c.class_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.level_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.period.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [councils, searchTerm])

  const filteredDecisions = useMemo(() => {
    if (!searchTerm) return decisions
    return decisions.filter(d => 
      d.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.class_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.decision.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [decisions, searchTerm])

  const stats = useMemo(() => ({
    total: councils.length,
    planned: councils.filter(c => c.status === 'planned').length,
    held: councils.filter(c => c.status === 'held').length,
    archived: councils.filter(c => c.status === 'archived').length,
    passage: decisions.filter(d => d.decision === 'passage').length,
    redoublement: decisions.filter(d => d.decision === 'redoublement').length,
    exclusion: decisions.filter(d => d.decision === 'exclusion').length,
  }), [councils, decisions])

  // Show warning if no school/year selected
  if (!selectedSchoolId || !selectedAcademicYearId) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-600">
            {lang === 'fr' ? 'Aucun établissement ou année scolaire sélectionné' : 'No school or academic year selected'}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {lang === 'fr' ? 'Veuillez sélectionner une année scolaire dans le header' : 'Please select an academic year in the header'}
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50/20 to-blue-50/20">
      <div className="page-header bg-white/80 backdrop-blur-md border-b border-amber-100">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-600 to-amber-700 rounded-xl flex items-center justify-center shadow-lg shadow-amber-200">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="page-title text-2xl font-bold bg-gradient-to-r from-amber-700 to-amber-900 bg-clip-text text-transparent">
                {lang === 'fr' ? 'Conseil de classe' : 'Class Council'}
              </h1>
              <p className="page-subtitle text-sm text-gray-500 mt-0.5">
                {lang === 'fr' 
                  ? `${stats.total} conseils \u2014 ${stats.held} tenus / ${stats.planned} planifiés` 
                  : `${stats.total} councils \u2014 ${stats.held} held / ${stats.planned} planned`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Badge année scolaire */}
            {selectedYearLabel && (
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                <School className="w-3 h-3 mr-1.5" />
                {selectedYearLabel}
              </span>
            )}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button 
                onClick={() => setActiveTab('councils')} 
                className={cn('px-4 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'councils' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}
              >
                {lang === 'fr' ? 'Conseils' : 'Councils'}
              </button>
              <button 
                onClick={() => setActiveTab('decisions')} 
                className={cn('px-4 py-2 rounded-md text-sm font-medium transition-all', activeTab === 'decisions' ? 'bg-white text-amber-700 shadow-sm' : 'text-gray-600 hover:text-gray-800')}
              >
                {lang === 'fr' ? 'Décisions' : 'Decisions'}
              </button>
            </div>
            <div className="relative">
              <input 
                type="text" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                placeholder={lang === 'fr' ? 'Rechercher...' : 'Search...'} 
                className="pl-4 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 w-64" 
              />
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-amber-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats.total}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Total' : 'Total'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-blue-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{stats.held}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Tenus' : 'Held'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats.passage}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Passages' : 'Promotions'}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-red-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">{stats.redoublement + stats.exclusion}</p>
                <p className="text-xs text-gray-500">{lang === 'fr' ? 'Redoublements/Exclusions' : 'Repetitions/Exclusions'}</p>
              </div>
            </div>
          </div>
        </div>

        {activeTab === 'councils' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-amber-600" />
                {lang === 'fr' ? 'Liste des conseils de classe' : 'Class council list'}
              </h2>
              <span className="text-sm text-gray-500">{filteredCouncils.length} {lang === 'fr' ? 'résultat(s)' : 'result(s)'}</span>
            </div>
            
            {filteredCouncils.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Classe' : 'Class'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Niveau' : 'Level'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Période' : 'Period'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Date' : 'Date'}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Statut' : 'Status'}</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Décisions' : 'Decisions'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredCouncils.map((council) => (
                      <tr key={council.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{council.class_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{council.level_name} \u2014 {council.cycle_name}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            {council.period}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{new Date(council.meeting_date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={cn(
                            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
                            council.status === 'planned' && 'bg-blue-50 text-blue-700 border-blue-200',
                            council.status === 'held' && 'bg-emerald-50 text-emerald-700 border-emerald-200',
                            council.status === 'archived' && 'bg-gray-50 text-gray-700 border-gray-200'
                          )}>
                            {council.status === 'planned' && (lang === 'fr' ? 'Planifié' : 'Planned')}
                            {council.status === 'held' && (lang === 'fr' ? 'Tenu' : 'Held')}
                            {council.status === 'archived' && (lang === 'fr' ? 'Archivé' : 'Archived')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center text-sm font-medium text-gray-700">{council.decisions_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  {lang === 'fr' ? 'Conseils de classe' : 'Class Councils'}
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {lang === 'fr' 
                    ? 'Cette page permettra de planifier, tenir et archiver les conseils de classe avec les décisions (passage, redoublement, exclusion).' 
                    : 'This page will allow planning, holding, and archiving class councils with decisions (promotion, repetition, exclusion).'}
                </p>
                <p className="text-sm text-amber-600 mt-4 font-medium">
                  {lang === 'fr' ? 'Aucun conseil trouvé pour cette année scolaire' : 'No councils found for this academic year'}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'decisions' && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-600" />
                {lang === 'fr' ? 'Décisions des conseils' : 'Council decisions'}
              </h2>
              <span className="text-sm text-gray-500">{filteredDecisions.length} {lang === 'fr' ? 'résultat(s)' : 'result(s)'}</span>
            </div>
            
            {filteredDecisions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Élève' : 'Student'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Classe' : 'Class'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Décision' : 'Decision'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Période' : 'Period'}</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? 'Date' : 'Date'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredDecisions.map((decision) => (
                      <tr key={decision.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium text-gray-900">{decision.student_name}</td>
                        <td className="py-3 px-4 text-sm text-gray-700">{decision.class_name}</td>
                        <td className="py-3 px-4">
                          <span className={cn(
                            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border',
                            getDecisionColor(decision.decision)
                          )}>
                            {getDecisionIcon(decision.decision)}
                            {decision.decision_label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">{decision.period}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{new Date(decision.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16 text-gray-400">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  {lang === 'fr' ? 'Décisions' : 'Decisions'}
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {lang === 'fr' 
                    ? 'Les décisions des conseils de classe (passage, redoublement, exclusion) seront listées ici.' 
                    : 'Class council decisions (promotion, repetition, exclusion) will be listed here.'}
                </p>
                <p className="text-sm text-amber-600 mt-4 font-medium">
                  {lang === 'fr' ? 'Aucune décision trouvée pour cette année scolaire' : 'No decisions found for this academic year'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}